import $ from 'jquery'
import Handlebars from 'handlebars'
import { Toast, deleteAllToasts } from 'toaster-js'
import { getCart, changeItem, addItemFromForm } from '../utils/theme-cart'
import { formatMoney } from '../utils/theme-currency'

const SELECTORS = {
    form: 'form[action^="/cart/add"]',
    cartDrawer: '#CartDrawer',
    cartTemplate: '#CartTemplate',
    cartContainer: '[data-ajax-cart-container]',
    cartBtn: '[data-cart-toggle-btn]',
    cartBackBtn: '[data-cart-back-btn]',
    cartBtnItemCount: '[data-cart-toggle-btn-itemcount]',
    addToCartBtn: '[data-add-to-cart-btn]',
    productAdjustQtyBtn: '[data-ajaxcart-qty-adjust-btn]',
    productAdjustQtyInput: '[data-ajaxcart-qty-adjust-input]',
    productRemoveBtn: '[data-product-remove-btn]'
}

const CLASSES = {
    updating: 'updating',
    cartClosing: 'cart-closing',
    cartOpening: 'cart-opening',
    cartOpen: 'cart-open',
    menuClosing: 'menu-closing',
    menuOpening: 'menu-opening',
    menuOpen: 'menu-open',
    sidepanelClosing: 'sidepanel-closing',
    sidepanelOpening: 'sidepanel-opening',
    sidepanelOpen: 'sidepanel-open',
    btnLoading: 'btn--loading'
}

const EVENTS = {
    drawerOpen: 'drawer:open'
}

const CLOSE_DURATION = 250

class Cart {
    constructor() {
        this.state = {
            isCartPanelOpen: false,
            isUpdating: false
        }
        this.closeTimeout = null

        this.$form = $(SELECTORS.form)
        this.$cartDrawer = $(SELECTORS.cartDrawer)
        this.$cartContainer = $(SELECTORS.cartContainer, this.$cartDrawer)
        this.$cartBtn = $(SELECTORS.cartBtn)
        this.$cartBackBtn = $(SELECTORS.cartBackBtn)
        this.$cartBtnItemCount = $(SELECTORS.cartBtnItemCount, this.$cartBtn)

        this.updateCart = this.updateCart.bind(this)
        this.addItem = this.addItem.bind(this)
        this.changeItemQty = this.changeItemQty.bind(this)
        this.onClickOutsideDrawer = this.onClickOutsideDrawer.bind(this)
        this.onDrawerOpen = this.onDrawerOpen.bind(this)

        this.init()
    }

    init() {
        this.cart = window.cart

        if (
            !this.$cartDrawer.length ||
            !this.$cartContainer.length ||
            !$(SELECTORS.cartTemplate).length
        ) {
            return
        }

        this.bindEvents()
        this.renderCart()
    }

    bindEvents() {
        // eslint-disable-next-line consistent-this
        const _this = this
        const $body = $(document.body)
        const animationEndEvents = [
            'webkitAnimationEnd',
            'mozAnimationEnd',
            'MSAnimationEnd',
            'oanimationend',
            'animationend'
        ].join(' ')

        // cart toggle button
        this.$cartBtn.click((event) => {
            event.preventDefault()
            event.stopPropagation()
            this.toggleCartDrawer()
        })
        this.$cartBtn.on(animationEndEvents, () => {
            this.$cartBtn.removeClass('animate')
        })

        // cart back button
        this.$cartBackBtn.click(() => {
            this.toggleCartDrawer(false)
        })

        // catch add-to-cart form submissions
        this.$form.on('submit', function (event) {
            event.preventDefault()
            const $form = $(this)
            _this.addItem($form.serializeJSON())
        })

        // ===== DELEGATED Events because Items reload in the cart

        $body.on('beforeUpdate.cart', () => {
            this.$cartDrawer.addClass(CLASSES.updating)
        })

        $body.on('afterUpdate.cart', () => {
            this.$cartDrawer.removeClass(CLASSES.updating)
        })
        document.addEventListener(EVENTS.drawerOpen, this.onDrawerOpen)

        // update item qty (plus/minus button)
        this.$cartContainer.on('click', SELECTORS.productAdjustQtyBtn, function () {
            const key = $(this).data('key')
            const qty = $(this).data('qty')
            _this.changeItemQty(key, qty)
        })

        // update item qty (input)
        this.$cartContainer.on('change', SELECTORS.productAdjustQtyInput, function () {
            const $input = $(this)
            const key = $input.data('key')
            const qty = parseInt($input.val().replace(/\D/g, ''), 10)

            if (qty >= 0) {
                _this.changeItemQty(key, qty)
            }
        })

        // remove item
        this.$cartContainer.on('click', SELECTORS.productRemoveBtn, function () {
            const key = $(this).data('key')
            _this.changeItemQty(key, 0)
        })
    }

    onBeforeUpdate() {
        this.state.isUpdating = true
        $(document.body).trigger('beforeUpdate.cart')
    }

    onAfterUpdate() {
        this.state.isUpdating = false
        $(document.body).trigger('afterUpdate.cart')
    }

    onClickOutsideDrawer(event) {
        if (!$(event.target).closest(this.$cartDrawer).length) {
            this.toggleCartDrawer(false)
        }
    }

    onDrawerOpen(event) {
        if (event.detail.drawer !== 'cart' && this.state.isCartPanelOpen) {
            this.toggleCartDrawer(false)
        }
    }

    toggleCartDrawer(force = null) {
        const isOpen = force === null ? !this.state.isCartPanelOpen : force
        const $body = $(document.body)

        if (isOpen) {
            clearTimeout(this.closeTimeout)
            document.dispatchEvent(
                new CustomEvent(EVENTS.drawerOpen, {
                    detail: {
                        drawer: 'cart'
                    }
                })
            )
            this.$cartDrawer.focus()
            $body.removeClass(
                `${CLASSES.sidepanelClosing} ${CLASSES.cartClosing} ${CLASSES.menuClosing} ${CLASSES.menuOpening}`
            )
            $body.addClass(`${CLASSES.sidepanelOpening} ${CLASSES.cartOpening}`)
            void this.$cartDrawer[0].offsetHeight
            $body.addClass(`${CLASSES.sidepanelOpen} ${CLASSES.cartOpen}`)
            $body.removeClass(`${CLASSES.sidepanelOpening} ${CLASSES.cartOpening}`)
            document.addEventListener('click', this.onClickOutsideDrawer)
        } else {
            this.$cartBtn.focus()
            $body.addClass(`${CLASSES.sidepanelClosing} ${CLASSES.cartClosing}`)
            $body.removeClass(CLASSES.cartOpen)
            document.removeEventListener('click', this.onClickOutsideDrawer)
            this.closeTimeout = setTimeout(() => {
                $body.removeClass(CLASSES.cartClosing)

                if (!$body.hasClass(CLASSES.menuOpen)) {
                    $body.removeClass(
                        `${CLASSES.sidepanelOpen} ${CLASSES.sidepanelClosing} ${CLASSES.sidepanelOpening} ${CLASSES.cartOpening} ${CLASSES.menuOpening}`
                    )
                }
            }, CLOSE_DURATION)
        }

        this.$cartBtn.attr('aria-expanded', String(isOpen))
        this.state.isCartPanelOpen = isOpen
    }

    getItemData(item, index) {
        /* Hack to get product image thumbnail
         *   - If image is not null
         *     - Remove file extension, add _small, and re-add extension
         *     - Create server relative link
         *   - A hard-coded url of no-image
         */
        let prodImg =
            '//cdn.shopify.com/s/assets/admin/no-image-medium-cc9732cb976dd349a0df1d39816fbcc7.gif'
        if (item.image != null) {
            prodImg = item.image.replace(/(\.[^.]*)$/, '_small$1').replace('http:', '')
        }

        // Create item's data object
        return {
            key: item.key,
            line: index + 1,
            url: item.url,
            img: prodImg,
            name: item.product_title,
            variation: item.variant_title,
            properties: item.properties,
            itemAdd: item.quantity + 1,
            itemMinus: item.quantity - 1,
            itemQty: item.quantity,
            price: formatMoney(item.price, theme.settings.moneyFormat),
            linePrice: formatMoney(item.line_price, theme.settings.moneyFormat),
            originalLinePrice: formatMoney(item.original_line_price, theme.settings.moneyFormat),
            discounts: item.discounts,
            discountsApplied: item.line_price !== item.original_line_price
        }
    }

    addItem(data) {
        if (this.state.isUpdating || !data.id) {
            return
        }

        const $addBtn = $(`#AddToCart-${data.id}`)
        $addBtn.addClass(CLASSES.btnLoading)

        this.onBeforeUpdate()
        addItemFromForm(data)
            .done((cart) => {
                this.updateCart(cart)
                this.displayCartNotification(data)
                $addBtn.removeClass(CLASSES.btnLoading)
            })
            .fail((error) => this.displayCartError(error))
    }

    changeItemQty(key, qty) {
        if (this.state.isUpdating || !key) {
            return
        }
        this.onBeforeUpdate()
        changeItem(key, qty).done(this.updateCart)
    }

    updateCart(cart) {
        if (cart && cart.token) {
            this.cart = cart

            this.updateCartButton()
            this.renderCart()
            this.onAfterUpdate()
        } else {
            getCart().done(this.updateCart)
        }
    }

    updateCartButton() {
        const itemcount = this.cart.item_count || 0

        const updateItemCount = () => {
            if (itemcount) {
                this.$cartBtnItemCount.empty().html(itemcount).removeClass('hidden')
            } else {
                this.$cartBtnItemCount.addClass('hidden')
            }
        }

        this.$cartBtn.addClass('animate')
        window.setTimeout(updateItemCount, 200)
    }

    displayCartNotification(data) {
        // data contains id and quantity of the product that was just added.

        // build notificaton
        const strings = window.theme.strings
        const qty = parseInt(data.quantity, 10)

        const message =
            qty > 1 ? strings.itemsAdded.other.replace('{{ count }}', qty) : strings.itemsAdded.one

        const $notificationContent = $(
            `<div>
                <p><strong>${message}</strong></p>
                <p class="text--right"><a href="#">${strings.viewCart} »</a></p>
            </div>`
        )

        // remove all previous toasts
        deleteAllToasts()

        // show notification
        const toast = new Toast($notificationContent.get(0), Toast.TYPE_MESSAGE, 6000)
        const onNotificationClick = (event) => {
            event.preventDefault()
            event.stopPropagation()
            this.toggleCartDrawer(true)
            toast.delete()
        }
        $notificationContent.on('click', onNotificationClick)
    }

    displayCartError(error) {
        // an error occured
        this.onAfterUpdate()
        if (error.status === 422 && Boolean(error.message)) {
            window.alert(error.description)
        }
    }

    renderCart() {
        if (!this.$cartContainer.length) return

        this.$cartContainer.empty()

        if (this.cart.item_count === 0) {
            // empty cart
            const emptyString = window.theme.strings.emptyCart || ''
            this.$cartContainer.append(
                `<p class="ajaxcart__empty">
                    ${emptyString}<br /><br />
                    <a href="/collections/all">Zum Shop</a>
                </p>`
            )
            return
        }

        // Handlebars.js cart layout
        const source = $(SELECTORS.cartTemplate).html()
        if (!source) return

        const template = Handlebars.compile(source)
        const items = this.cart.items.map(this.getItemData)

        // Gather all cart data and add to DOM
        const data = {
            items,
            note: this.cart.note,
            totalPrice: formatMoney(this.cart.total_price, theme.settings.moneyFormat),
            totalCartDiscount: 0,
            totalCartDiscountApplied: this.cart.total_discount !== 0
        }

        this.$cartContainer.append(template(data))
    }
}

$.fn.serializeJSON = function () {
    const obj = {}
    const arr = this.serializeArray()
    $.each(arr, function () {
        if (obj[this.name]) {
            if (!obj[this.name].push) {
                obj[this.name] = [obj[this.name]]
            }
            obj[this.name].push(this.value || '')
        } else {
            obj[this.name] = this.value || ''
        }
    })
    return obj
}

$(document).ready(() => new Cart())
